import { useEffect, useRef, useState } from 'react';
import { getUserSession } from '@/lib/rpc/utils/auth';

type ScheduleFn = (cb: () => void | Promise<void>, delay: number) => number;
type ClearFn = (id: number) => void;

export function startBackoffPolling(options: {
  run: () => Promise<boolean>;
  baseDelayMs: number;
  maxBackoffMs: number;
  minBackoffMs?: number;
  schedule: ScheduleFn;
  clear: ClearFn;
  shouldStop?: () => boolean;
}) {
  const {
    run,
    baseDelayMs,
    maxBackoffMs,
    minBackoffMs = 2000,
    schedule,
    clear,
    shouldStop,
  } = options;

  let stopped = false;
  let backoffMs = 0;
  let timeoutId: number | null = null;

  const stop = () => {
    stopped = true;
    if (timeoutId !== null) {
      clear(timeoutId);
      timeoutId = null;
    }
  };

  const scheduleNext = () => {
    if (stopped || shouldStop?.()) return;

    const delay = baseDelayMs + backoffMs;
    timeoutId = schedule(async () => {
      if (stopped || shouldStop?.()) return;

      const success = await run().catch(() => false);

      if (stopped || shouldStop?.()) return;

      if (success) {
        backoffMs = 0;
      } else {
        backoffMs = backoffMs === 0
          ? Math.min(maxBackoffMs, minBackoffMs)
          : Math.min(maxBackoffMs, backoffMs * 2);
      }

      scheduleNext();
    }, delay);
  };

  scheduleNext();

  return {
    stop,
  };
}

type UseSessionState =
  | { status: 'loading'; user: null }
  | { status: 'recovering'; user: { id: string; email?: string } | null }
  | { status: 'anon';    user: null }
  | { status: 'authed';  user: { id: string; email?: string } };

/**
 * Надежный хук сессии с RPC-first, событийной синхронизацией и anti-flicker
 * Три состояния: loading (неопределенно), authed (есть сессия), anon (нет сессии)
 */
export function useSession(): UseSessionState {
  const [state, setState] = useState<UseSessionState>({ status: 'loading', user: null });
  const inited = useRef(false);
  const inflightRef = useRef(false);
  const timeoutIdRef = useRef<number | null>(null);
  const fastRetryTimeoutRef = useRef<number | null>(null);
  const pollHandleRef = useRef<{ stop: () => void } | null>(null);
  const bcRef = useRef<BroadcastChannel | null>(null);
  const lastVisibilityRefreshRef = useRef(0);
  const lastStateLogRef = useRef<string>('none');

  useEffect(() => {
    // Защита от StrictMode double-mount
    if (inited.current) return;
    inited.current = true;

    let cancelled = false;

    const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

    const toRecovering = (prev: UseSessionState): UseSessionState =>
      (prev.status === 'authed' || prev.status === 'recovering')
        ? { status: 'recovering', user: prev.user }
        : prev;

    // Deduplicated session snapshot
    const snapshot = async (reason = 'unknown') => {
      if (inflightRef.current || cancelled) return true;
      inflightRef.current = true;
      let success = true;

      try {
        const sessionRes = await getUserSession();
        success = sessionRes.ok;

        if (cancelled) return success;

        // ✅ Сброс таймера "anon fallback" при любом результате
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }

        if (!sessionRes.ok) {
          setState(toRecovering);
          if (isBrowser) window.setTimeout(() => { if (!cancelled) snapshot('recovering-retry'); }, 600);
          return success;
        }

        const user = sessionRes.data?.user ?? null;
        let shouldFastRetry = false;

        setState(prev => {
          let next: UseSessionState;

          if (user?.id) {
            next = { status: 'authed', user: { id: user.id, email: user.email } };
          } else {
            const wasTrustedUser =
              (prev.status === 'authed' || prev.status === 'recovering') && prev.user !== null;

            if (wasTrustedUser) {
              shouldFastRetry = true;
              next = { status: 'recovering', user: prev.user };
            } else {
              next = { status: 'anon', user: null };
            }
          }

          // ✅ Не трогаем стейт, если статус не изменился (prevent re-renders)
          if (prev.status === next.status &&
              prev.user?.id === next.user?.id) {
            return prev;
          }
          
          // Only log status changes, not every snapshot
          const logKey = `${prev.status}->${next.status}`;
          if (lastStateLogRef.current !== logKey) {
            lastStateLogRef.current = logKey;
          }
          return next;
        });

        if (shouldFastRetry && isBrowser) {
          if (fastRetryTimeoutRef.current) {
            window.clearTimeout(fastRetryTimeoutRef.current);
          }
          fastRetryTimeoutRef.current = window.setTimeout(() => {
            if (!cancelled) snapshot('recovering-fast-retry');
          }, 250);
        }

      } catch (err) {
        success = false;
        if (!cancelled) {
          setState(toRecovering);
          if (isBrowser) window.setTimeout(() => { if (!cancelled) snapshot('recovering-error'); }, 600);
        }
      } finally {
        inflightRef.current = false;
      }

      return success;
    };

    // ✅ Стартовый снимок
    snapshot('initial');

    // ✅ Event-driven update через BroadcastChannel
    if (isBrowser && typeof BroadcastChannel !== 'undefined') {
      try {
        bcRef.current = new BroadcastChannel('auth');
        bcRef.current.onmessage = (e) => {
          if (e?.data?.type === 'session-updated') {
            snapshot('broadcast-event');
          }
        };
      } catch {
        /* BroadcastChannel not available */
      }
    }

    // ✅ Visibility change refresh with throttling (15s minimum)
    let onVisibilityChange: (() => void) | null = null;
    if (isBrowser) {
      onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && !cancelled) {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastVisibilityRefreshRef.current;

          if (timeSinceLastRefresh > 15000) { // 15s throttle
            lastVisibilityRefreshRef.current = now;
            snapshot('visibility-change');
          }
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);

      // ✅ Поллинг с backoff и дедупликацией
      pollHandleRef.current = startBackoffPolling({
        run: () => snapshot('poll'),
        baseDelayMs: 15000,
        maxBackoffMs: 30000,
        schedule: (cb, delay) => window.setTimeout(cb as TimerHandler, delay),
        clear: (id) => window.clearTimeout(id),
        shouldStop: () => cancelled,
      });

      // ✅ Fallback timeout (увеличен до 4с, сбрасывается при успешном ответе)
      timeoutIdRef.current = window.setTimeout(() => {
        if (!cancelled && !inflightRef.current) {
          setState(prev =>
            prev.status === 'loading'
              ? { status: 'anon', user: null }
              : prev
          );
        }
      }, 4000);
    }

    // Cleanup
    return () => {
      cancelled = true;

      if (bcRef.current) {
        bcRef.current.close();
        bcRef.current = null;
      }
      
      if (isBrowser && onVisibilityChange) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      if (fastRetryTimeoutRef.current) {
        clearTimeout(fastRetryTimeoutRef.current);
        fastRetryTimeoutRef.current = null;
      }

      if (pollHandleRef.current) {
        pollHandleRef.current.stop();
        pollHandleRef.current = null;
      }
    };
  }, []);

  return state;
}